import { useCallback, useEffect, useState } from 'react';
import { api, type ParkSettingsDto } from '../lib/api';
import { makeFormats, toDay } from '../lib/format';
import { useI18n, useT, type MessageKey } from '../lib/i18n';
import { MAX_REPLY_HOURS } from '@shared/index';

/**
 * The park's own settings.
 *
 * ---------------------------------------------------------------------------
 * WHAT BELONGS HERE, AND THE LINE THAT MATTERS
 * ---------------------------------------------------------------------------
 *
 * Everything on this screen is either **who the park is** (where enquiries go,
 * which country a booking form opens on) or **how values are written on a
 * screen** (date order, clock, currency label, timezone).
 *
 * Nothing here changes what is STORED or TRANSMITTED. A date in the database and
 * on the wire is ISO 8601, every date the engine handles is a day number, and
 * timestamps are UTC — none of that is a setting and none of it should be, because
 * two systems agreeing on `2027-07-03` is what makes an import, an export and a
 * channel feed possible at all.
 *
 * ---------------------------------------------------------------------------
 * WHY EVERY FIELD SHOWS ITS EFFECT
 * ---------------------------------------------------------------------------
 *
 * `dmy` and `mdy` are jargon, and the difference between them is whether
 * `03-07-2027` is July or March — four months, on a screen reception reads all
 * day. So each option is shown as the date it produces, worked out with the same
 * formatter the rest of the dashboard uses. An operator picks the one that looks
 * right rather than the one whose abbreviation they recognise.
 */

export interface SettingsProps {
  run: <T>(fn: () => Promise<T>, ok?: string | ((out: T) => string)) => Promise<T | null>;
  /** So the whole dashboard re-formats the moment this is saved. */
  onSaved: (settings: ParkSettingsDto) => void;
  busy?: boolean;
}

const DATE_FORMATS = ['locale', 'dmy', 'mdy', 'iso'] as const;
const TIME_FORMATS = ['24h', '12h'] as const;

const DATE_LABEL: Record<(typeof DATE_FORMATS)[number], MessageKey> = {
  locale: 'settings.dateLocale',
  dmy: 'settings.dateDmy',
  mdy: 'settings.dateMdy',
  iso: 'settings.dateIso',
};

/**
 * Zones offered by name, with anything already stored kept.
 *
 * A free-text IANA field is a field somebody types `Amsterdam` into and then
 * cannot work out why it was refused. A short list covers the parks this serves,
 * and a zone set by an import or by hand survives because it is added to the list
 * rather than silently replaced by the first option.
 */
const ZONES = [
  'UTC',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Zurich',
];

/** A day and an instant with nothing special about them, for the previews. */
const SAMPLE_DAY = toDay('2027-07-03')!;
const SAMPLE_INSTANT = '2027-07-03T14:05:00Z';

export function Settings({ run, onSaved, busy }: SettingsProps) {
  const t = useT();
  // The INTERFACE language, which is what `App` formats the dashboard with. The
  // park's `defaultLocale` is a different thing — the language a GUEST page opens
  // in — and previewing with that would show the operator a date they will never
  // see on their own screen.
  const { lang } = useI18n();
  const [form, setForm] = useState<ParkSettingsDto | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [pw, setPw] = useState({ current: '', next: '', again: '' });
  const [pwProblem, setPwProblem] = useState<string | null>(null);


  const load = useCallback(async () => {
    const s = await api.parkSettings();
    setForm(s);
    return s;
  }, []);

  useEffect(() => { void run(load); }, [run, load]);

  if (!form) return <section className="panel"><p className="note">{t('settings.loading')}</p></section>;

  const set = <K extends keyof ParkSettingsDto>(key: K, value: ParkSettingsDto[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  /** The formatter this form would produce, for the previews. */
  const preview = makeFormats({
    dateFormat: form.dateFormat,
    timeFormat: form.timeFormat,
    timeZone: form.timeZone,
    currency: form.currency,
    locale: lang,
  });

  const changePassword = async () => {
    setPwProblem(null);
    // Both checks are repeated at the API. They are here as well because the
    // API cannot tell a typo from a decision, and a 400 on a submitted form is
    // a worse way to learn you typed it twice differently.
    if (pw.next !== pw.again) {
      setPwProblem(t('settings.passwordMismatch'));
      return;
    }
    if (pw.next.length < 12) {
      setPwProblem(t('settings.passwordShort'));
      return;
    }
    const out = await run(
      () => api.changePassword(pw.current, pw.next),
      t('settings.passwordChanged'),
    );
    // Cleared only on success, so a wrong current password does not also cost
    // the operator the new one they had just typed correctly.
    if (out) setPw({ current: '', next: '', again: '' });
  };

  const save = async () => {
    setProblem(null);
    const email = (form.inquiryEmail ?? '').trim();
    // Checked here as well as at the API, because the consequence is invisible:
    // an address with a typo does not fail, it simply means the park stops being
    // told about enquiries and finds out weeks later.
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setProblem(t('settings.badEmail'));
      return;
    }
    const currency = (form.currency ?? '').trim();
    if (currency && !/^[A-Za-z]{3}$/.test(currency)) {
      setProblem(t('settings.badCurrency'));
      return;
    }
    const out = await run(
      () => api.putParkSettings({
        ...form,
        inquiryEmail: email || undefined,
        currency: currency ? currency.toUpperCase() : undefined,
      }),
      t('settings.saved'),
    );
    if (out) {
      setForm(out);
      onSaved(out);
    }
  };

  return (
    <section className="panel">
      <h2>{t('settings.heading')}</h2>
      {/* Nothing here touches storage or the wire: dates are always kept as
          YYYY-MM-DD and timestamps in UTC, so an export, an import or a
          channel feed reads the same whatever is chosen. */}
      <p className="note">{t('settings.intro')}</p>

      {problem && <div className="banner bad" role="alert">{problem}</div>}

      <fieldset className="card">
        <legend>{t('settings.parkHeading')}</legend>
        <div className="row">
          <label className="wide">
            {t('settings.inquiryEmail')}
            <input
              type="email" value={form.inquiryEmail ?? ''} maxLength={200}
              onChange={(e) => set('inquiryEmail', e.target.value)}
            />
          </label>
        </div>
        <p className="note">{t('settings.inquiryEmailNote')}</p>
        {/*
          How fast the park says it answers.

          A NUMBER, not a sentence, and the note under it says why: a guest
          reads their receipt in their own language, so a sentence typed here
          would reach English and German guests in Dutch. Empty is a real
          answer — the guest is then told a person will reply and nothing about
          when, which is what this said before anyone could set it, except that
          it also invented "a couple of working days".
        */}
        <div className="row">
          <label>
            {t('settings.replyWithin')}
            <input
              type="number" min={1} max={MAX_REPLY_HOURS} step={1} size={6}
              value={form.replyWithinHours ?? ''}
              onChange={(e) => set('replyWithinHours',
                e.target.value === '' ? undefined : Number(e.target.value))}
            />
          </label>
        </div>
        <p className="note">{t('settings.replyWithinNote')}</p>
        <div className="row">
          <label>
            {t('settings.defaultCountry')}
            <input
              type="text" value={form.defaultCountry ?? ''} maxLength={2} size={4}
              onChange={(e) => set('defaultCountry', e.target.value.toUpperCase())}
            />
          </label>
          {/* Not "Language": the header already has one, and it means the
              language of THIS screen. Two controls with one label on one page,
              meaning different things, is a setting somebody changes by mistake. */}
          <label>
            {t('settings.defaultLocale')}
            <select
              value={form.defaultLocale ?? ''}
              onChange={(e) => set('defaultLocale', e.target.value || undefined)}
            >
              <option value="">{t('settings.localeUnset')}</option>
              <option value="nl">Nederlands</option>
              <option value="en">English</option>
            </select>
          </label>
        </div>
        <p className="note">{t('settings.defaultLocaleNote')}</p>
      </fieldset>

      <fieldset className="card">
        <legend>{t('settings.displayHeading')}</legend>
        <p className="note">{t('settings.displayNote')}</p>

        <div className="row">
          <label>
            {t('settings.dateFormat')}
            <select
              value={form.dateFormat ?? 'locale'}
              onChange={(e) => set('dateFormat', e.target.value as ParkSettingsDto['dateFormat'])}
            >
              {DATE_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {/* The option says what it DOES. "dmy" is jargon, and the
                      difference between it and "mdy" is four months. */}
                  {`${t(DATE_LABEL[f])} — ${makeFormats({
                    dateFormat: f, locale: lang,
                  }).shortDate(SAMPLE_DAY)}`}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('settings.timeFormat')}
            <select
              value={form.timeFormat ?? '24h'}
              onChange={(e) => set('timeFormat', e.target.value as ParkSettingsDto['timeFormat'])}
            >
              {TIME_FORMATS.map((f) => (
                <option key={f} value={f}>
                  {`${f} — ${makeFormats({
                    timeFormat: f, timeZone: form.timeZone, locale: lang,
                  }).dateTime(SAMPLE_INSTANT)}`}
                </option>
              ))}
            </select>
          </label>
          <label>
            {t('settings.timeZone')}
            <select
              value={form.timeZone ?? 'UTC'}
              onChange={(e) => set('timeZone', e.target.value)}
            >
              {/* A stored zone that is not on the list is added to it, so an
                  import or a hand edit is not quietly overwritten by whichever
                  option happens to come first. */}
              {(ZONES.includes(form.timeZone ?? 'UTC') ? ZONES : [form.timeZone!, ...ZONES])
                .map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </label>
          <label>
            {t('settings.currency')}
            <input
              type="text" value={form.currency ?? ''} maxLength={3} size={5}
              placeholder="EUR"
              onChange={(e) => set('currency', e.target.value.toUpperCase())}
            />
          </label>
        </div>

        <p className="note" role="status">
          {t('settings.preview', {
            date: preview.longDate(SAMPLE_DAY),
            stamp: preview.dateTime(SAMPLE_INSTANT),
            amount: preview.money(1234.5),
          })}
        </p>
        <p className="note">{t('settings.timeZoneNote')}</p>
        <p className="note">{t('settings.currencyNote')}</p>
      </fieldset>

      <div className="row">
        <button type="button" className="btn primary" disabled={busy} onClick={save}>
          {t('settings.save')}
        </button>
      </div>

      {/* Below the save button, with a button of its own, because a password
          field inside the settings form is a password that gets sent by the
          Save button somebody pressed for the currency. */}
      <fieldset className="card">
        <legend>{t('settings.passwordHeading')}</legend>
        <p className="note">{t('settings.passwordNote')}</p>

        {pwProblem && <div className="banner bad" role="alert">{pwProblem}</div>}

        <div className="row">
          <label>
            {t('settings.passwordCurrent')}
            <input
              type="password" autoComplete="current-password"
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
            />
          </label>
          <label>
            {t('settings.passwordNext')}
            <input
              type="password" autoComplete="new-password"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
            />
          </label>
          {/* Asked twice because there is nothing to fall back on: a mistyped
              new password locks the only operator out of the only account, and
              this product has no reset-by-email to undo it with. */}
          <label>
            {t('settings.passwordAgain')}
            <input
              type="password" autoComplete="new-password"
              value={pw.again} onChange={(e) => setPw({ ...pw, again: e.target.value })}
            />
          </label>
        </div>
        <p className="note">{t('settings.passwordRule')}</p>

        <div className="row">
          <button
            type="button" className="btn" disabled={busy || !pw.current || !pw.next}
            onClick={changePassword}
          >
            {t('settings.passwordChange')}
          </button>
        </div>
      </fieldset>
    </section>
  );
}
